import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import ArcoVue from '@arco-design/web-vue'
import ArcoVueIcon from '@arco-design/web-vue/es/icon'
import App from './App.vue'
import router from './router'
import i18n from './i18n'
import { setupVueQuery } from '@/utils/query'

// 核心架构工具导入
import { createModuleLogger } from './utils/logger'

// FontAwesome 配置
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
// Solid icons
import {
  faHome,
  faUser,
  faCog,
  faSignOutAlt,
  faChartBar,
  faBell,
  faShip,
  faPlane,
  faWarehouse,
  faFileInvoiceDollar,
  faCheckCircle,
  faGlobe,
  faClock,
  faShieldAlt,
  faBuilding,
  faHistory,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faUsers,
  faTrophy,
  faHandshake,
  faRocket,
  faTruck,
  faNewspaper,
  faCalendarAlt,
  faEye,
  faEyeSlash,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faArrowLeft,
  faArrowRight,
  faArrowDown,
  faTag,
  faLink,
  faBookmark,
  faBookOpen,
  faBars,
  faTimes,
  faLock,
  faComments,
  faPaperPlane,
  faHeart,
  faStar,
  faThumbsUp,
  faShare,
  faDownload,
  faUpload,
  faSearch,
  faFilter,
  faSort,
  faEdit,
  faTrash,
  faPlus,
  faMinus,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faQuestionCircle,
  faSpinner,
  faRefresh,
  faCalendar,
  faFileAlt,
  faClipboardList,
  faFileInvoice,
  faUndo,
  faFileUpload,
  faList,
  faSignInAlt,
  faUserCheck,
  faPlayCircle,
  faMousePointer,
  // New icons for portalnew
  faTriangleExclamation,
  faShieldHalved,
  faCloudArrowUp,
  faCircleNotch,
  faFileCircleXmark,
  faRectangleAd,
  faUserSecret,
  faCircleQuestion,
  faCommentDots,
  faRobot,
  faSun,
  faMoon,
  faAnchor,
  faMagnifyingGlassDollar,
  faHeadset,
  faMapLocationDot,
  faTableList,
  faRoute,
  faCube,
  faTrain,
  faBoxOpen,
  faBox
} from '@fortawesome/free-solid-svg-icons'
// Brand icons
import {
  faWeixin,
  faTiktok,
  faFacebook,
  faInstagram,
  faYoutube,
  faLinkedin,
  faXTwitter,
  faTwitter,
  faWeibo,
  faQq,
  faWhatsapp
} from '@fortawesome/free-brands-svg-icons'

// 添加图标到库
library.add(
  // Solid icons
  faHome,
  faUser,
  faCog,
  faSignOutAlt,
  faChartBar,
  faBell,
  faShip,
  faPlane,
  faWarehouse,
  faFileInvoiceDollar,
  faCheckCircle,
  faGlobe,
  faClock,
  faShieldAlt,
  faBuilding,
  faHistory,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faUsers,
  faTrophy,
  faHandshake,
  faRocket,
  faTruck,
  faNewspaper,
  faCalendarAlt,
  faEye,
  faEyeSlash,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faArrowLeft,
  faArrowRight,
  faArrowDown,
  faTag,
  faLink,
  faBookmark,
  faBookOpen,
  faBars,
  faTimes,
  faLock,
  faComments,
  faPaperPlane,
  faHeart,
  faStar,
  faThumbsUp,
  faShare,
  faDownload,
  faUpload,
  faSearch,
  faFilter,
  faSort,
  faEdit,
  faTrash,
  faPlus,
  faMinus,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faQuestionCircle,
  faSpinner,
  faRefresh,
  faCalendar,
  faFileAlt,
  faClipboardList,
  faFileInvoice,
  faUndo,
  faFileUpload,
  faList,
  faSignInAlt,
  faUserCheck,
  faPlayCircle,
  faMousePointer,
  // New icons for portalnew
  faTriangleExclamation,
  faShieldHalved,
  faCloudArrowUp,
  faCircleNotch,
  faFileCircleXmark,
  faRectangleAd,
  faUserSecret,
  faCircleQuestion,
  faCommentDots,
  faRobot,
  faSun,
  faMoon,
  faAnchor,
  faMagnifyingGlassDollar,
  faHeadset,
  faMapLocationDot,
  faTableList,
  faRoute,
  faCube,
  faTrain,
  faBoxOpen,
  faBox,
  // Brand icons
  faWeixin,
  faTiktok,
  faFacebook,
  faInstagram,
  faYoutube,
  faLinkedin,
  faXTwitter,
  faTwitter,
  faWeibo,
  faQq,
  faWhatsapp
)

// 样式导入
import '@arco-design/web-vue/dist/arco.css'
import './styles/tailwind.css'
import './styles/index.scss'
import './styles/portal/brand-overrides.css'

// 创建应用日志器
const appLogger = createModuleLogger('APP')

// 环境变量检查（使用架构化日志）
if (!import.meta.env.VITE_API_BASE_URL) {
  appLogger.warn('缺少 VITE_API_BASE_URL 环境变量')
}

// 创建应用实例
const app = createApp(App)

// 创建 Pinia 实例
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

// 安装插件
app.use(ArcoVue)
app.use(ArcoVueIcon)
app.use(pinia)
app.use(router)
app.use(i18n)

// 注册FontAwesome组件
app.component('FontAwesomeIcon', FontAwesomeIcon)

// 全局错误处理
app.config.errorHandler = (err: any, instance: any, info: any) => {
  appLogger.error('全局错误捕获', err as Error)
  console.error('Vue Error:', err, info)
}

// 全局警告处理
app.config.warnHandler = (msg: any, instance: any, trace: any) => {
  appLogger.warn('Vue警告', { message: msg, trace })
}

// 初始化 TanStack Query
setupVueQuery(app)

// 应用启动日志
appLogger.info('应用启动中...', {
  environment: import.meta.env.MODE,
  version: '1.0.0'
})

// 挂载应用
app.mount('#app')

appLogger.info('应用启动完成', {
  mountTime: performance.now()
})

// 开发环境下的调试信息
if (import.meta.env.DEV) {
  appLogger.debug('开发环境调试信息', {
    environment: import.meta.env.MODE,
    apiUrl: import.meta.env.VITE_API_BASE_URL
  })
}
